import { useEffect } from 'react';
import { toast } from '@/components/ui/Toast';
import { emitTelemetry } from '@/lib/telemetry';

/**
 * One-time remap toast (DD1 / A60.25).
 * Shown on first session after ENT-60 ships, informs users that:
 *   - Cmd-K now opens Global Search (not Ask)
 *   - Ask is now on Cmd-J
 *
 * Dismissed state is persisted in localStorage so the toast is never shown again
 * after the user clicks dismiss (even across browser sessions).
 */
const TOAST_KEY = 'ent-60.shortcut-remap-toast.v1';

/**
 * U2 — a migration notice is aimed at someone who used the OLD binding. Before this flag, "not yet
 * dismissed" was the only condition checked, which is equally true for a returning user who hasn't
 * clicked dismiss AND a brand-new account's first-ever login — the exact case reported (a fresh
 * agency's first login, `A06b`). Set the first time this hook runs at all, on ANY browser; its
 * absence means this is that browser's first-ever authenticated session, so the toast is skipped
 * (without touching TOAST_KEY, so it remains eligible on this browser's next login).
 *
 * Per-BROWSER, not per-account — a long-time user's first visit on a new device also misses the
 * toast once there, and every existing user's browser lacks this flag on the single day this ships
 * (so everyone is under-notified once on rollout day). Both are the safer direction for a one-time
 * historical notice than showing it to every future first-time signup forever.
 */
const EVER_AUTHENTICATED_KEY = 'ent-60.ever-authenticated.v1';

/**
 * StrictMode (dev only) invokes an effect twice per real mount — mount, cleanup, mount again — to
 * surface effects that aren't idempotent. This one wasn't: the first invocation would set
 * EVER_AUTHENTICATED_KEY and skip the toast (correct), but the second invocation then read that
 * SAME flag the first one just wrote and concluded "returning session," showing the toast anyway
 * — reproduced live against localhost:5173 (dev server), not merely a theoretical concern. This
 * module-level guard makes the whole effect body a no-op on that second call. It does not survive
 * an actual page reload (a fresh module instance resets it), so it only suppresses the
 * StrictMode-only double-invoke, never a genuinely new session.
 */
let effectRanThisPageLoad = false;

export function useShortcutRemapToast(): void {
  useEffect(() => {
    if (effectRanThisPageLoad) return;
    effectRanThisPageLoad = true;

    // Already dismissed — never show again
    if (localStorage.getItem(TOAST_KEY)) return;

    if (!localStorage.getItem(EVER_AUTHENTICATED_KEY)) {
      localStorage.setItem(EVER_AUTHENTICATED_KEY, '1');
      return;
    }

    const shownAt = Date.now();

    toast('⌘K now opens Global Search. Ask moved to ⌘J.', {
      duration: Infinity, // user must explicitly dismiss
      onDismiss: () => {
        localStorage.setItem(TOAST_KEY, '1');
        emitTelemetry('global_search.shortcut_remap_toast_dismissed', {
          secondsToDismiss: Math.round((Date.now() - shownAt) / 1000),
        });
      },
      onAutoClose: () => {
        localStorage.setItem(TOAST_KEY, '1');
      },
    });

    emitTelemetry('global_search.shortcut_remap_toast_shown', {});
  }, []); // run once on mount — intentional empty deps
}
